import i18next from 'i18next';
import { VehicleData } from '../../@types/codegen/types';
import { EnhancedProperty, ItemCategory } from '../../@types/vehicle-properties';
import deepCopy from '../deep-copy';

export const TEXT_TYPE = 'text';
export const NUMBER_TYPE = 'number';
export const BASIC_CATEGORY: ItemCategory = Object.freeze({
  id: 1000,
  name: i18next.t('category.basic') || 'basic',
  subCategory: null,
});
export const ENGINE_SUBCATEGORY: ItemCategory = Object.freeze({
  id: 6,
  name: i18next.t('category.engineAndPerformance') || 'engineAndPerformance',
  subCategory: {
    id: 54,
    name: i18next.t('subCategory.engine') || 'engine',
    subCategory: null,
  },
});

class PropertyBuilder {
  name: string = '';
  value: string | number = '';
  type: string = 'unknown';
  unit: string | null = null;
  hint: string | null = null;
  category: ItemCategory | null = null;

  withName(name: string) {
    this.name = name;
    return this;
  }

  withHint(hint: string) {
    if (hint) {
      this.hint = hint;
    }
    return this;
  }

  withValue(value: string | number) {
    this.value = value;
    return this;
  }

  withType(type: string) {
    this.type = type;
    return this;
  }

  withCategory(category: ItemCategory) {
    this.category = category;
    return this;
  }

  withUnit(unit: string) {
    this.unit = unit;
    return this;
  }

  build(): EnhancedProperty {
    return {
      name: this.name,
      value: this.value,
      type: this.type,
      unit: this.unit,
      hint: this.hint,
      category: this.category,
    };
  }
}

export default (item: VehicleData, properties: { [key: string]: EnhancedProperty }) => {
  const newProperties = deepCopy(properties);

  if (!!item.manufacturer) {
    newProperties.manufacturer = new PropertyBuilder()
      .withName(i18next.t('customProperty.manufacturer.displayName') || 'manufacturer')
      .withValue(item.manufacturer)
      .withType(TEXT_TYPE)
      .withCategory(BASIC_CATEGORY)
      .build();
  }

  if (!!item.modelSeries) {
    newProperties.modelSeries = new PropertyBuilder()
      .withName(i18next.t('customProperty.modelSeries.displayName') || 'modelSeries')
      .withValue(item.modelSeries)
      .withType(TEXT_TYPE)
      .withCategory(BASIC_CATEGORY)
      .build();
  }

  if (!!item.modelName) {
    newProperties.modelName = new PropertyBuilder()
      .withName(i18next.t('customProperty.modelName.displayName') || 'modelName')
      .withValue(item.modelName)
      .withHint(i18next.t('customProperty.modelName.hint'))
      .withType(TEXT_TYPE)
      .withCategory(BASIC_CATEGORY)
      .build();
  }

  if (!!item.modelYear) {
    newProperties.modelYear = new PropertyBuilder()
      .withName(i18next.t('customProperty.modelYear.displayName') || 'modelYear')
      .withValue(item.modelYear)
      .withHint(i18next.t('customProperty.modelYear.hint'))
      .withType(NUMBER_TYPE)
      .withCategory(BASIC_CATEGORY)
      .build();
  }

  if (!!item.manufactureYear) {
    newProperties.manufactureYear = new PropertyBuilder()
      .withName(i18next.t('customProperty.manufactureYear.displayName') || 'manufactureYear')
      .withValue(item.manufactureYear)
      .withHint(i18next.t('customProperty.manufactureYear.hint'))
      .withType(NUMBER_TYPE)
      .withCategory(BASIC_CATEGORY)
      .build();
  }

  if (item.odometerReading) {
    newProperties.mileage = new PropertyBuilder()
      .withName(i18next.t('customProperty.odometer.displayName') || 'odometer')
      .withValue(item.odometerReading.value)
      .withHint(i18next.t('customProperty.odometer.hint'))
      .withType(NUMBER_TYPE)
      .withCategory(BASIC_CATEGORY)
      .withUnit(i18next.t(`odometer.${item.odometerReading.unit}`))
      .build();
  } else if (!!item.mileage) {
    newProperties.mileage = new PropertyBuilder()
      .withName(i18next.t('customProperty.odometer.displayName') || 'odometer')
      .withValue(item.mileage)
      .withHint(i18next.t('customProperty.odometer.hint'))
      .withType(NUMBER_TYPE)
      .withCategory(BASIC_CATEGORY)
      .withUnit(i18next.t('odometer.ScandinavianMile'))
      .build();
  }

  if (!!item.gearboxType) {
    newProperties.gearboxType = new PropertyBuilder()
      .withName(i18next.t('customProperty.gearboxType.displayName') || 'gearboxType')
      .withValue(item.gearboxType)
      .withType(TEXT_TYPE)
      .withCategory(BASIC_CATEGORY)
      .build();
  }

  if (!!item.registrationNumber) {
    newProperties.registrationNumber = new PropertyBuilder()
      .withName(i18next.t('customProperty.registrationNumber.displayName') || 'registrationNumber')
      .withValue(item.registrationNumber)
      .withType(TEXT_TYPE)
      .withCategory(BASIC_CATEGORY)
      .build();
  }

  if (!!item.fuelType) {
    newProperties.fuelType = new PropertyBuilder()
      .withName(i18next.t('customProperty.fuelType.displayName') || 'fuelType')
      .withValue(item.fuelType)
      .withHint(i18next.t('customProperty.fuelType.hint'))
      .withType(TEXT_TYPE)
      .withCategory(BASIC_CATEGORY)
      .build();
  }

  if (!!item.properties?.numberOfDoors) {
    newProperties.fuelType = new PropertyBuilder()
      .withName(i18next.t('customProperty.numberOfDoors.displayName') || 'numberOfDoors')
      .withValue(item.properties.numberOfDoors as number)
      .withType(NUMBER_TYPE)
      .withCategory(BASIC_CATEGORY)
      .build();
  }

  if (item.enginePower) {
    newProperties.enginePower = new PropertyBuilder()
      .withName(i18next.t('customProperty.enginePower.displayName') || 'enginePower')
      .withValue(item.enginePower)
      .withUnit(i18next.t('customProperty.enginePower.unit'))
      .withType(NUMBER_TYPE)
      .withCategory(ENGINE_SUBCATEGORY)
      .build();
  }

  return newProperties;
};
